import React, { useEffect } from 'react';
import { useTranslation } from 'react-i18next';
import { useLocation, useNavigate } from 'react-router-dom';
import WithProcessError from 'hocs/WithProcessError';
import { previewPanelStyle } from 'pages/constants';

import Button from 'components/Button/Button';
import ContentFooter from 'components/ContentFooter/ContentFooter';
import Loader from 'components/Loader/Loader';
import LoadingErrorFallback from 'components/LoadingErrorFallback/LoadingErrorFallback';
import NeedTerminal from 'components/NeedTerminal/NeedTerminal';
import Panel from 'components/Panel/Panel';
import PanelHeader from 'components/PanelHeader/PanelHeader';
import PaymentForm from 'components/PaymentForm/PaymentForm';
import Popup from 'components/Popup/Popup';
import Snackbar from 'components/Snackbar/Snackbar';

import ProductCard from './ProductCard';

import Add24 from 'icons/Add24';

import useApi from 'utils/api';
import { useAuth } from 'utils/auth';
import declOfNum from 'utils/declOfNum';
import { actions } from 'utils/permissions';

import styles from './payment-pages.scss';

function Products(props) {
    const navigate = useNavigate();
    const location = useLocation();
    const api = useApi();
    const auth = useAuth();
    const { t } = useTranslation();

    const [previewPage, setPreviewPage] = React.useState(null);
    const [snack, setSnack] = React.useState(null);
    const [popup, setPopup] = React.useState(null);
    const [forms, setForms] = React.useState([]);
    const [loading, setLoading] = React.useState(false);
    const [pagesLoadingError, setPagesLoadingError] = React.useState(false);
    const [terminal, setTerminal] = React.useState(null);

    const { processError } = props;

    const [terminalsMap, setTerminalsMap] = React.useState({});

    function handleCreateClick() {
        window.ym(91769012, 'reachGoal', 'create_product');
        navigate('./create', { state: { pageId: previewPage?.id || null } });
    }

    /**
     * Restore preview object on page reload.
     * @returns Form object.
     */
    function restorePreviewPage() {
        if (location?.state && location?.state?.pageId) {
            return getPreviewPage(location.state.pageId);
        }

        return null;
    }

    /**
     * Finds form for preview.
     * @param {number} formId Form id.
     * @returns Form object.
     */
    function getPreviewPage(formId) {
        return forms.find((form) => form.id === formId) || null;
    }

    /**
     * Handles click on page card to setup preview.
     * @param {number} pageId Page id.
     */
    function handlePreview(pageId) {
        setPreviewPage(getPreviewPage(pageId));
    }

    /**
     * Copies payment page link to buffer.
     */
    function handleLinkCopied() {
        if (snack) return;
        setSnack(<Snackbar text="Ссылка скопирована" delay={1000} onClosed={handleSnackbarClosed} />);
    }

    function handleSnackbarClosed() {
        setSnack(null);
    }

    function handlePopupClosed() {
        setPopup(null);
    }

    function removePageById(id) {
        const pageToDelete = forms.findIndex((item) => item.id === id);
        const currentForms = [...forms];
        currentForms.splice(pageToDelete, 1);
        setForms(currentForms);
    }

    function handleDeleteConfirm(formId) {
        const { id } = auth.company;
        api.deleteForm(id, formId)
            .then((data) => {
                if (data.id === formId) {
                    removePageById(formId);
                } else {
                    processError('Не удалось удалить страницу');
                }
            })
            .catch((error) => processError('Не удалось удалить страницу', error));
    }

    function handleDeleteClick(pageId) {
        setPopup(
            <Popup
                title="Удалить страницу"
                onClosed={handlePopupClosed}
                autoclose={true}
                canCloseOutside={true}
                onConfirm={() => handleDeleteConfirm(pageId)}
                confirmDanger={true}
                maxWidth={380}
                description={'Вы уверены, что хотите удалить страницу? Вы не сможете её восстановить.'}
                confirmText={'Удалить'}
            ></Popup>
        );
    }

    /**
     * Load Forms from the server.
     */
    function getForms() {
        setLoading(true);

        const { id } = auth?.company;

        if (!id) {
            setForms([]);
            setPagesLoadingError(true);
            setLoading(false);

            return false;
        }

        api.getForms(id)
            .then((data) => {
                const { forms } = data;
                setForms(forms);
                setPagesLoadingError(false);
            })
            .catch((error) => {
                setPagesLoadingError(true);
                console.error(error);
            })
            .finally(() => {
                setLoading(false);
            });
    }

    /**
     * Tries to find any terminal in the company.
     * If not found – error will be displayed.
     */
    function checkTerminal() {
        const companyId = auth?.company.id;

        api.getPaymentServices(companyId)
            .then((data) => {
                const { payment_services } = data;

                /**
                 * Terminals map used to display payment service icon.
                 */
                const terminalsToServiceMap = {};
                payment_services.forEach((service) => {
                    terminalsToServiceMap[service.terminal_id] = service.type;
                });
                setTerminalsMap(terminalsToServiceMap);

                /**
                 * If we don't have terminals, show alert.
                 */
                const availableTerminalIds = payment_services.filter((ps) => Boolean(ps.terminal_id));
                console.log(availableTerminalIds);

                if (availableTerminalIds.length === 0) {
                    setTerminal(<NeedTerminal />);
                } else if (availableTerminalIds.length === 1) {
                    /**
                     * If only one terminal found and it is Yookassa terminal, we need to check
                     * is this terminal ready for use. If not, we should display alert.
                     */
                    const yookassaPaymentService = payment_services.find((ps) => ps.type === 'Yookassa');

                    if (yookassaPaymentService && yookassaPaymentService.terminal_id) {
                        api.getTerminal(companyId, yookassaPaymentService.terminal_id)
                            .then((result) => {
                                if (!result.ready) {
                                    setTerminal(<NeedTerminal />);
                                }
                            })
                            .catch((error) => {
                                processError('Не удалось получить информацию о терминале', error);
                            });
                    }
                }
            })
            .catch((error) => {
                console.error(error);
            });
    }

    /**
     * Get message for Pages panel footer.
     * @returns Message for pages panel footer.
     */
    function getPagesFooterContent() {
        const count = forms.length === 0 ? 'Нет' : forms.length;
        const entity = declOfNum(forms.length, [
            t('products.footer.product1'),
            t('products.footer.product2'),
            t('products.footer.product5'),
        ]);

        return `${count} ${entity}`;
    }

    useEffect(() => {
        getForms();
        checkTerminal();
    }, []);

    /**
     * Triggers on every forms loads.
     */
    useEffect(() => {
        setPreviewPage(restorePreviewPage());
    }, [forms]);

    return (
        <>
            {popup}
            {snack}
            <Panel
                fill={false}
                contentStyles={{ maxWidth: 420, minWidth: 420 }}
                header={
                    <PanelHeader
                        title={t('place.products')}
                        after={
                            <Button
                                text={t('products.action.create')}
                                needPermission={actions.CREATE_FORMS}
                                disabled={terminal !== null || loading}
                                icon={<Add24 />}
                                onClick={handleCreateClick}
                                level={'secondary'}
                            />
                        }
                    />
                }
            >
                {terminal}
                <div className={styles.pages}>
                    {loading && (
                        <div className={styles.loader}>
                            <Loader dark={true} />
                        </div>
                    )}
                    {pagesLoadingError && (
                        <LoadingErrorFallback text={t('products.listLoadingFailed')} onRetry={getForms} />
                    )}
                    {forms.map((data) => (
                        <ProductCard
                            key={data.id}
                            data={data}
                            terminalType={terminalsMap[data.terminal_id]}
                            selected={data.id === previewPage?.id}
                            onClick={handlePreview}
                            onDelete={handleDeleteClick}
                            onLinkCopied={handleLinkCopied}
                        />
                    ))}
                </div>
                <ContentFooter>{getPagesFooterContent()}</ContentFooter>
            </Panel>
            <Panel fill={true} style={previewPanelStyle} separated={true}>
                {previewPage === null && (
                    <div style={{ height: '100%', display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                        <ContentFooter>{t('products.previewPlaceholder')}</ContentFooter>
                    </div>
                )}
                {previewPage !== null && <PaymentForm data={previewPage} previewMode={true} />}
            </Panel>
        </>
    );
}

Products.propTypes = {
    ...WithProcessError.propTypes,
};

export default WithProcessError(Products);
